import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import type { ExperienceLevel, Gender, Locale, OnboardingIntent } from '../api/types';
import { GenderPicker } from '../components/GenderPicker';
import { MainPracticePicker } from '../components/MainPracticePicker';
import { TraditionPicker } from '../components/TraditionPicker';
import { useAuth } from '../auth/AuthContext';
import { useUser } from '../state/UserContext';
import { setAppLanguage } from '../i18n';
import { ALL_THEME_IDS, THEME_DISPLAY_NAMES, THEMES, type Tradition, type ThemeId } from '../theme/tokens';

type Tab = 'profile' | 'traditions' | 'appearance' | 'billing';

const TABS: { id: Tab; labelKey: string; icon: string }[] = [
  { id: 'profile', labelKey: 'settings.tabs.profile', icon: '👤' },
  { id: 'traditions', labelKey: 'settings.tabs.traditions', icon: '🧭' },
  { id: 'appearance', labelKey: 'settings.tabs.appearance', icon: '🎨' },
  { id: 'billing', labelKey: 'settings.tabs.billing', icon: '💳' },
];

// Each language's own name, in its own script -- shown in every locale identically (a language
// picker labels its options in the native name, not translated into whatever's currently active).
const LOCALE_NAMES: Record<Locale, string> = { en: 'English', es: 'Español', hi: 'हिन्दी', zh: '中文' };
const ALL_LOCALES: Locale[] = ['en', 'es', 'hi', 'zh'];

export function Settings() {
  const { me, updateMe, refreshQuiet } = useUser();
  const { logout } = useAuth();
  const { t } = useTranslation();
  const [localeSaving, setLocaleSaving] = useState<Locale | null>(null);

  const [tab, setTab] = useState<Tab>(() =>
    new URLSearchParams(window.location.search).get('upgraded') ? 'billing' : 'profile'
  );
  const [displayName, setDisplayName] = useState(me?.displayName ?? '');
  const [traditions, setTraditions] = useState<Tradition[]>(me?.traditions ?? []);
  const [primaryTradition, setPrimaryTradition] = useState<Tradition | null>(me?.primaryTradition ?? null);
  const [gender, setGender] = useState<Gender | null>(me?.gender ?? null);
  const [intent, setIntent] = useState<OnboardingIntent | null>(me?.intent ?? null);
  const [experienceLevel, setExperienceLevel] = useState<ExperienceLevel | null>(me?.experienceLevel ?? null);
  const [timeBudgetMinutes, setTimeBudgetMinutes] = useState<number | null>(me?.timeBudgetMinutes ?? null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [themeSaving, setThemeSaving] = useState<ThemeId | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [returnedFromCheckout, setReturnedFromCheckout] = useState(
    () => new URLSearchParams(window.location.search).get('upgraded') === '1'
  );

  // Coming back from a real Whop checkout: the redirect itself never grants Premium (only a
  // verified webhook does, see backend/app/handlers/billing.py) -- so poll GET /api/me a few
  // times rather than trusting the URL, in case the webhook hasn't landed yet.
  const polledRef = useRef(false);
  useEffect(() => {
    if (!returnedFromCheckout || polledRef.current) return;
    polledRef.current = true;
    window.history.replaceState({}, '', window.location.pathname);
    let cancelled = false;
    (async () => {
      for (let attempt = 0; attempt < 6 && !cancelled; attempt++) {
        const updated = await refreshQuiet();
        if (updated?.tier === 'premium') return;
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [returnedFromCheckout, refreshQuiet]);

  const handleUpgrade = async () => {
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const { checkoutUrl } = await api.createCheckout();
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
      // Mock mode applies the upgrade immediately with no external URL to redirect to.
      await refreshQuiet();
      setReturnedFromCheckout(true);
    } catch (e) {
      setCheckoutError(e instanceof Error ? e.message : 'Could not start checkout.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const effectivePrimary =
        traditions.length === 1
          ? traditions[0]
          : traditions.includes(primaryTradition as Tradition)
            ? primaryTradition
            : null;
      await updateMe({
        displayName,
        traditions,
        primaryTradition: effectivePrimary,
        gender,
        intent,
        experienceLevel,
        timeBudgetMinutes,
      });
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleLeaderboardToggle = async () => {
    if (!me) return;
    setError(null);
    try {
      await updateMe({ leaderboardOptIn: !me.leaderboardOptIn });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update your leaderboard setting.');
    }
  };

  const handleThemeSelect = async (themePreference: ThemeId) => {
    if (!me || me.themePreference === themePreference) return;
    setError(null);
    setThemeSaving(themePreference);
    try {
      await updateMe({ themePreference });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update your theme.');
    } finally {
      setThemeSaving(null);
    }
  };

  const handleLocaleSelect = async (locale: Locale) => {
    if (!me || me.locale === locale) return;
    setError(null);
    setLocaleSaving(locale);
    try {
      await updateMe({ locale });
      setAppLanguage(locale);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update your language.');
    } finally {
      setLocaleSaving(null);
    }
  };

  const previewTradition = primaryTradition ?? (traditions.length === 1 ? traditions[0] : null);

  return (
    <div>
      <h1 className="page-title">{t('settings.title')}</h1>

      <div className="settings-tabs">
        {TABS.map((tabItem) => (
          <button
            key={tabItem.id}
            type="button"
            className={`settings-tab${tab === tabItem.id ? ' active' : ''}`}
            onClick={() => setTab(tabItem.id)}
          >
            <span aria-hidden="true">{tabItem.icon}</span> {t(tabItem.labelKey)}
          </button>
        ))}
      </div>

      {tab === 'profile' && (
        <div className="settings-panel">
          <div className="field">
            <label htmlFor="displayName">Display name</label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
            />
          </div>

          <div className="section-title">Your practice</div>
          <p className="page-subtitle" style={{ marginBottom: 12 }}>
            Shapes your Today recommendation on Home -- editable any time, not just at signup.
          </p>
          <div className="field">
            <label htmlFor="intent">What brings you here</label>
            <select
              id="intent"
              value={intent ?? ''}
              onChange={(e) => setIntent((e.target.value || null) as OnboardingIntent | null)}
            >
              <option value="">Prefer not to say</option>
              <option value="deepen">Deepen my tradition</option>
              <option value="explore">Explore spirituality</option>
              <option value="routine">Build my routine</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="experienceLevel">Your experience</label>
            <select
              id="experienceLevel"
              value={experienceLevel ?? ''}
              onChange={(e) => setExperienceLevel((e.target.value || null) as ExperienceLevel | null)}
            >
              <option value="">Prefer not to say</option>
              <option value="new">New to this</option>
              <option value="some">Some experience</option>
              <option value="experienced">Experienced</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="timeBudget">Daily time budget (minutes)</label>
            <input
              id="timeBudget"
              type="number"
              min={3}
              max={120}
              value={timeBudgetMinutes ?? ''}
              onChange={(e) => setTimeBudgetMinutes(e.target.value ? Number(e.target.value) : null)}
              placeholder="No limit"
            />
          </div>

          <div className="section-title">Your guide</div>
          <p className="page-subtitle" style={{ marginBottom: 12 }}>
            Who greets you on Home and walks you through your practices.
          </p>
          <GenderPicker selected={gender} onChange={setGender} tradition={previewTradition} />

          <div className="toggle-row">
            <div>
              <div className="toggle-row-label">Leaderboard opt-in</div>
              <div className="practice-item-meta">Show your streak on the public leaderboard.</div>
            </div>
            <button
              type="button"
              className={`switch${me?.leaderboardOptIn ? ' on' : ''}`}
              role="switch"
              aria-checked={Boolean(me?.leaderboardOptIn)}
              onClick={handleLeaderboardToggle}
              disabled={!me}
            >
              <span className="knob" />
            </button>
          </div>
        </div>
      )}

      {tab === 'traditions' && (
        <div className="settings-panel">
          <p className="page-subtitle" style={{ marginBottom: 12 }}>
            These shape your daily quotes, practices, and library.
          </p>
          <TraditionPicker selected={traditions} onChange={setTraditions} />

          {traditions.length > 1 && (
            <>
              <div className="section-title">Main practice</div>
              <p className="page-subtitle" style={{ marginBottom: 12 }}>
                The one your guide will be dressed for, and what we&rsquo;ll feature first on
                Home. The rest still shape your quotes and practices as secondary traditions.
              </p>
              <MainPracticePicker options={traditions} selected={primaryTradition} onChange={setPrimaryTradition} />
            </>
          )}
        </div>
      )}

      {tab === 'appearance' && (
        <div className="settings-panel">
          <p className="page-subtitle" style={{ marginBottom: 12 }}>
            Your theme is independent of your traditions &mdash; pick whichever palette feels
            right. Saved instantly.
          </p>
          <div className="theme-grid">
            {ALL_THEME_IDS.map((id) => {
              const tokens = THEMES[id];
              const isSelected = me?.themePreference === id;
              return (
                <button
                  key={id}
                  type="button"
                  className={`theme-option${isSelected ? ' selected' : ''}`}
                  onClick={() => handleThemeSelect(id)}
                  disabled={!me || themeSaving !== null}
                  aria-pressed={isSelected}
                >
                  <span className="theme-option-swatches" aria-hidden="true">
                    <span className="swatch" style={{ background: tokens.primary }} />
                    <span className="swatch" style={{ background: tokens.secondary }} />
                    <span className="swatch" style={{ background: tokens.background }} />
                  </span>
                  <span className="theme-option-name">{THEME_DISPLAY_NAMES[id]}</span>
                  {isSelected && <span className="theme-option-check" aria-hidden="true">&#10003;</span>}
                </button>
              );
            })}
          </div>

          <div className="section-title" style={{ marginTop: 24 }}>{t('settings.language')}</div>
          <p className="page-subtitle" style={{ marginBottom: 12 }}>{t('settings.languageHelp')}</p>
          <div className="theme-grid">
            {ALL_LOCALES.map((locale) => {
              const isSelected = (me?.locale ?? 'en') === locale;
              return (
                <button
                  key={locale}
                  type="button"
                  className={`theme-option${isSelected ? ' selected' : ''}`}
                  onClick={() => handleLocaleSelect(locale)}
                  disabled={!me || localeSaving !== null}
                  aria-pressed={isSelected}
                >
                  <span className="theme-option-name">{LOCALE_NAMES[locale]}</span>
                  {isSelected && <span className="theme-option-check" aria-hidden="true">&#10003;</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'billing' && (
        <div className="settings-panel">
          <p className="page-subtitle" style={{ marginBottom: 12 }}>
            Premium is $9.99/month, billed monthly only. Deeper levels in every pillar (beyond
            the first 5) require Premium; the free tier stays a real, complete introduction, not
            a locked demo.
          </p>

          <div className="themed-card" style={{ marginBottom: 16 }}>
            <div className="toggle-row-label" style={{ marginBottom: 4 }}>
              Current plan: {me?.tier === 'premium' ? 'Premium' : 'Free'}
            </div>
            {me?.tier === 'premium' ? (
              <p className="practice-item-meta">
                Thanks for supporting Omnist. To cancel or update payment details, use the
                receipt/manage-subscription link from your Whop confirmation email.
              </p>
            ) : (
              <p className="practice-item-meta">
                Upgrading opens Whop&rsquo;s secure checkout in this browser. Purchases are web-only
                &mdash; the mobile app never sells in-app, so nothing is marked up for App Store fees.
              </p>
            )}
          </div>

          {returnedFromCheckout && me?.tier !== 'premium' && (
            <p className="page-subtitle" style={{ marginBottom: 12 }}>
              Thanks! This can take a few moments to reflect here after checkout.
            </p>
          )}

          {checkoutError && <p className="error-text">{checkoutError}</p>}

          {me?.tier !== 'premium' && (
            <button
              type="button"
              className="btn btn-primary btn-block"
              onClick={handleUpgrade}
              disabled={checkoutLoading}
            >
              {checkoutLoading ? 'Starting checkout...' : 'Upgrade to Premium — $9.99/month'}
            </button>
          )}
        </div>
      )}

      {error && <p className="error-text">{error}</p>}
      {saved && <p className="saved-text">Saved.</p>}

      {tab !== 'appearance' && tab !== 'billing' && (
        <button type="button" className="btn btn-primary btn-block" onClick={handleSave} disabled={saving} style={{ marginTop: 16 }}>
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      )}

      <button type="button" className="btn btn-ghost btn-block" onClick={logout} style={{ marginTop: 12 }}>
        Log out
      </button>
    </div>
  );
}
